import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    // View Model always starts OFF (product image) on a fresh page load —
    // the state is deliberately NOT persisted across pages (2026-08-12; the
    // earlier sessionStorage restore made later PLPs land with it ON).
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Whether the View Model (on-model / slide 1) view is currently active.
   * @returns {boolean}
   */
  get viewModelOn() {
    return this.getAttribute('data-view-model') === 'on';
  }

  /**
   * Toggles the global "View Model" state for every product card in the grid.
   * OFF (default) shows the product image (slide 0); ON shows the card's
   * dedicated model slide — rendered by card-gallery.liquid from the
   * custom.model_image product metafield (slide-id "model-image"). Cards
   * without a model image stay on their product image. Bound from the toggle
   * button via `on:click="results-list/toggleViewModel"`.
   *
   * @param {Event} event
   */
  toggleViewModel = ({ target }) => {
    const next = !this.viewModelOn;
    this.#setViewModel(next);

    // Keep all View Model toggles in this section in sync (there can be more
    // than one instance rendered, e.g. desktop vs. a duplicated control).
    const toggles = this.querySelectorAll('.view-model-toggle');
    for (const toggle of toggles) {
      toggle.setAttribute('aria-pressed', String(next));
    }

    if (target instanceof HTMLElement) target.setAttribute('aria-pressed', String(next));
  };

  /**
   * Sets the View Model state and applies it to all current cards.
   * @param {boolean} on
   */
  #setViewModel(on) {
    this.setAttribute('data-view-model', on ? 'on' : 'off');
    this.#applyViewModel();
  }

  /**
   * Applies the current View Model state to every product card slideshow in
   * the grid. ON selects each card's "model-image" slide (metafield-driven);
   * cards without one — or with fewer than two slides — are left showing
   * their product image.
   *
   * @param {NodeListOf<Element> | Element[]} [scope] - Optional subset of card
   *   containers to apply to (used for newly appended pages). Defaults to the
   *   whole grid.
   */
  #applyViewModel(scope) {
    const { grid } = this.refs;
    if (!grid) return;

    const roots = scope ?? [grid];

    for (const root of roots) {
      const slideshows = /** @type {NodeListOf<import('slideshow').Slideshow>} */ (
        root.querySelectorAll('product-card slideshow-component')
      );

      for (const slideshow of slideshows) {
        // `slides` excludes hidden (e.g. non-selected variant) slides. Guard
        // cards that don't have a second slide to swap to.
        if (!slideshow.slides || slideshow.slides.length < 2) continue;

        if (this.viewModelOn) {
          // PJ-4833: query the DOM, not `slides` — on video products the
          // model slide renders [hidden] (outside the 2-asset swipe pair),
          // which `slides` excludes; select({id}) auto-reveals it.
          const hasModelSlide = !!slideshow.querySelector('slideshow-slide[slide-id="model-image"]');
          if (!hasModelSlide) continue;
          slideshow.select({ id: 'model-image' }, undefined, { animate: false });
        } else {
          slideshow.select(0, undefined, { animate: false });
        }
      }
    }
  }

  /**
   * Re-apply the current View Model state to cards appended by infinite scroll
   * or Load More, so newly loaded pages match the rest of the grid.
   *
   * @param {NodeListOf<Element> | Element[]} [items]
   */
  afterPageRender(items) {
    if (!this.viewModelOn) return;

    // Runs synchronously right after the appended cards enter the DOM, before
    // they first paint. Setting `initial-slide` (server-rendered as "0") uses
    // the slideshow's own attribute handling: already-initialized components
    // re-select in a microtask (pre-paint, so no slide-0 flash), and
    // not-yet-initialized ones honour it in their connect flow.
    for (const root of items ?? []) {
      const slideshows = /** @type {NodeListOf<import('slideshow').Slideshow>} */ (
        root.querySelectorAll('product-card slideshow-component')
      );

      for (const slideshow of slideshows) {
        const visibleSlides = slideshow.slides;
        if (!visibleSlides || visibleSlides.length < 2) continue;

        // Only cards with a metafield-driven model slide swap; the rest keep
        // their product image.
        const modelSlide = visibleSlides.find((slide) => slide.getAttribute('slide-id') === 'model-image');
        if (!modelSlide) continue;

        // `initial-slide` indexes ALL slides while `slides` excludes hidden
        // (non-selected variant) ones — map the model slide back to its raw
        // index.
        const rawIndex = slideshow.refs.slides?.indexOf(modelSlide) ?? -1;
        if (rawIndex < 1) continue;
        slideshow.setAttribute('initial-slide', String(rawIndex));
      }
    }
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
